import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  ArrowCounterClockwise,
  Chats,
  MonitorPlay,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { FeedbackWidget } from "./FeedbackWidget";
import { AccountChip } from "./AccountChip";
import { YayLogo } from "./YayLogo";
import type { ConnectionStatus } from "../lib/connection";
import type { RoomMedia, RoomMode } from "@yay/shared";

interface TopBarProps {
  viewerCount: number;
  status: ConnectionStatus;
  chatOpen: boolean;
  onToggleChat: () => void;
  mode: RoomMode;
  onToggleNerd: () => void;
  /** Nerd mode only — hide the video for flashcard-only layout. */
  videoHidden?: boolean;
  onToggleVideo?: () => void;
  /** Private rooms — compact YouTube URL control in the center. */
  media?: RoomMedia;
  canSetMedia?: boolean;
  onSetMedia?: (youtube: string) => void;
  mediaDisabled?: boolean;
  /** Nerd mode available — hidden in the global lobby. */
  canNerd?: boolean;
}

export function TopBar({
  viewerCount,
  status,
  chatOpen,
  onToggleChat,
  mode,
  onToggleNerd,
  videoHidden = false,
  onToggleVideo,
  media = { kind: "default" },
  canSetMedia = false,
  onSetMedia,
  mediaDisabled = false,
  canNerd = true,
}: TopBarProps) {
  const nerd = mode === "nerd";
  const label =
    status === "connected"
      ? `${viewerCount} ${nerd ? "studying" : "watching"}`
      : status === "connecting"
        ? "Connecting…"
        : status === "disconnected"
          ? "Reconnecting…"
          : `${viewerCount} ${nerd ? "studying" : "watching"}`;

  const urlInputRef = useRef<HTMLInputElement>(null);
  const [urlDraft, setUrlDraft] = useState(
    media.kind === "youtube" ? `https://youtu.be/${media.youtubeId}` : "",
  );

  useEffect(() => {
    setUrlDraft(
      media.kind === "youtube" && media.youtubeId
        ? `https://youtu.be/${media.youtubeId}`
        : "",
    );
  }, [media]);

  function submitMedia(e: FormEvent) {
    e.preventDefault();
    if (!canSetMedia || !onSetMedia || mediaDisabled) return;
    onSetMedia(urlDraft.trim());
  }

  return (
    <nav className={`top-bar${chatOpen ? " chat-open" : ""}`}>
      <div className="top-bar-stage">
        <div className="top-bar-left">
          <YayLogo variant="purple" className="brand-logo" />
          {canNerd && (
            <button
              type="button"
              className={`nerd-toggle${nerd ? " active" : ""}`}
              onClick={onToggleNerd}
              aria-pressed={nerd}
              aria-label={nerd ? "Exit nerd mode" : "Enter nerd mode"}
              title="nerd"
            >
              nerd
            </button>
          )}
        </div>

        {canSetMedia && (
          <div
            className="top-bar-center"
            onClick={() => urlInputRef.current?.focus()}
          >
            <div className="top-bar-center--media" />
          </div>
        )}
      </div>

      {canSetMedia && (
        <div
          className={`top-bar-url-slot${urlDraft.trim() ? " has-value" : ""}`}
        >
          <span className="top-bar-url-hint">
            Hover or click to paste a YouTube link
          </span>
          <form
            id="top-bar-url-form"
            className="top-bar-url"
            onSubmit={submitMedia}
          >
            <input
              ref={urlInputRef}
              type="url"
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              placeholder="Paste a YouTube link"
              disabled={mediaDisabled}
              aria-label="YouTube URL"
            />
            <button
              type="submit"
              className="top-bar-url-go"
              disabled={mediaDisabled}
              aria-label="Set YouTube link"
              title="Set"
            >
              <PaperPlaneTilt size={16} weight="bold" />
            </button>
          </form>
          {media.kind === "youtube" && (
            <button
              type="button"
              className="top-bar-url-go"
              disabled={mediaDisabled}
              aria-label="Clear YouTube link"
              title="Clear"
              onClick={() => onSetMedia?.("")}
            >
              <ArrowCounterClockwise size={16} weight="bold" />
            </button>
          )}
        </div>
      )}

      <div className="top-bar-right">
        <span className="viewer-count" data-status={status}>
          {label}
        </span>
        <button
          className={`collapse-toggle${chatOpen ? " chat-open" : ""}`}
          onClick={onToggleChat}
          aria-label={chatOpen ? "Hide chat" : "Show chat"}
        >
          <Chats size={16} weight={chatOpen ? "fill" : "regular"} />
        </button>
        {nerd && onToggleVideo && (
          <button
            type="button"
            className={`collapse-toggle${videoHidden ? "" : " chat-open"}`}
            onClick={onToggleVideo}
            aria-label={videoHidden ? "Show video" : "Hide video"}
            title={videoHidden ? "Show video" : "Hide video"}
          >
            <MonitorPlay size={16} weight={videoHidden ? "regular" : "fill"} />
          </button>
        )}
        <AccountChip />
        <FeedbackWidget />
      </div>
    </nav>
  );
}
